import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from './Section';
import Button from './Button';
import { FixelsHint } from './FixelsBranding';
import DemoModal, { DemoActionType } from './DemoModal';
import { Car, Shield, Sparkles, Banknote, RotateCcw, LucideIcon, ArrowRight, Mail, CheckCircle2 } from 'lucide-react';
import { SelectorState, CarType, Goal, Budget, TreatmentRecommendation } from '../types';

interface OptionCardProps {
  icon: LucideIcon | any;
  title: string;
  selected: boolean;
  onClick: () => void;
}

const OptionCard: React.FC<OptionCardProps> = ({ 
  icon: Icon, 
  title, 
  selected, 
  onClick 
}) => (
  <button
    onClick={onClick}
    className={`group relative flex flex-col items-center justify-center p-6 md:p-10 border transition-all duration-500 ease-out w-full overflow-hidden ${
      selected 
        ? 'bg-neutral-900 text-white border-white scale-[1.02] shadow-[0_0_30px_-5px_rgba(255,255,255,0.1)] z-10' 
        : 'bg-transparent border-white/10 text-neutral-500 hover:border-white/30 hover:text-neutral-300 hover:bg-white/5'
    }`}
  >
    {selected && <div className="absolute top-3 right-3 text-white"><CheckCircle2 size={16} /></div>}
    <Icon size={24} md:size={28} strokeWidth={1.5} className={`mb-4 md:mb-6 transition-colors duration-300 ${selected ? 'text-white' : 'text-neutral-500 group-hover:text-white'}`} />
    <span className="font-display uppercase tracking-widest text-xs">{title}</span>
  </button>
);

const TreatmentSelector: React.FC = () => {
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  
  const [state, setState] = useState<SelectorState>({
    step: 1,
    carType: null,
    goal: null,
    budget: null,
  });

  const nextStep = () => setState(prev => ({ ...prev, step: prev.step + 1 }));
  const reset = () => setState({ step: 1, carType: null, goal: null, budget: null });

  const getRecommendation = (): TreatmentRecommendation => {
    const { goal, budget } = state;

    if (goal === 'protection') {
      if (budget === 'premium') return {
        title: "Full Body PPF",
        description: "De ultieme bescherming. Volledige dekking met zelfherstellende Paint Protection Film. Onzichtbaar pantser tegen steenslag.",
        priceRange: "€3.500 - €5.500",
        features: ["10 jaar garantie", "Zelfherstellend", "Onzichtbare finish", "Max. Inruilwaarde"]
      };
      if (budget === 'mid') return {
        title: "Track Pack PPF + Coating",
        description: "Strategische bescherming. PPF op de 'high impact' zones (neus, spiegels) gecombineerd met glascoating op de rest.",
        priceRange: "€1.800 - €2.500",
        features: ["Impact zones beschermd", "Extreme glans", "Makkelijk wassen", "5 jaar garantie"]
      };
      return {
        title: "Glass Coating Pro",
        description: "Een semi-permanente binding met de lak. Creëert een extreem glad en waterafstotend oppervlak.",
        priceRange: "€800 - €1.200",
        features: ["Diepe glans", "Hydrofoob", "UV Filter", "3 jaar garantie"]
      };
    }

    if (goal === 'aesthetics') {
      if (budget === 'premium') return {
        title: "Color Change + Doorjambs",
        description: "Een complete transformatie. Nieuwe kleur inclusief sponningen voor een fabrieksorigineel resultaat.",
        priceRange: "€3.000 - €4.500",
        features: ["Unieke look", "Lakbescherming", "Omkeerbaar", "Premium Vinyl"]
      };
      return {
        title: "Exterior Wrap & Chrome Delete",
        description: "Moderniseer de uitstraling. Nieuwe kleur aan de buitenzijde en alle chroomlijsten in hoogglans zwart.",
        priceRange: "€2.000 - €3.000",
        features: ["Directe impact", "Stealth look", "Satijn / Mat / Gloss", "200+ kleuren"]
      };
    }

    // Resale
    return {
      title: "Concours Correctie",
      description: "Terug naar beter dan nieuw. Multi-stage polijsten om 95% van de lakdefecten te verwijderen.",
      priceRange: "€600 - €1.000",
      features: ["Showroom staat", "Krasvrij", "Diepte & Reflectie", "Interieur detail"]
    };
  };

  const handleConversion = () => {
    // Intercepted for Demo Experience
    setDemoModalOpen(true);
  };

  return (
    <Section id="selector" className="bg-neutral-950">
      <DemoModal 
        isOpen={demoModalOpen} 
        onClose={() => setDemoModalOpen(false)} 
        type="quote"
      />
      
      <div className="max-w-4xl mx-auto">
        
        {/* Header & Steps */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 border-b border-white/10 pb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-display text-white mb-2">TREATMENT SELECTOR</h2>
            <p className="text-neutral-500 text-sm">Configureer de ideale behandeling.</p>
          </div>
          <div className="flex gap-2 mt-6 md:mt-0">
             {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`h-1 w-8 md:w-12 transition-colors duration-500 ${state.step >= s ? 'bg-white' : 'bg-white/10'}`}
              />
            ))}
          </div>
        </div>

        {/* Container with min-height to prevent layout shift */}
        <div className="min-h-[500px] flex flex-col justify-between">
          <AnimatePresence mode="wait">
            {state.step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-grow flex flex-col"
              >
                <div className="mb-8 md:mb-10">
                  <span className="text-xs uppercase tracking-widest text-neutral-500">Stap 01 / 03</span>
                  <h3 className="text-xl md:text-2xl text-white font-display mt-2">SELECTEER VOERTUIG</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
                  {(['coupe', 'sedan', 'suv', 'supercar'] as CarType[]).map((type) => (
                    <OptionCard 
                      key={type}
                      icon={Car}
                      title={type}
                      selected={state.carType === type}
                      onClick={() => setState(p => ({ ...p, carType: type }))}
                    />
                  ))}
                </div>
                <div className="flex justify-end mt-auto pt-4 md:pt-12">
                  <Button onClick={nextStep} disabled={!state.carType} className="w-full md:w-auto">
                    Volgende Stap <ArrowRight size={16} />
                  </Button>
                </div>
              </motion.div>
            )}

            {state.step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-grow flex flex-col"
              >
                 <div className="mb-8 md:mb-10">
                  <span className="text-xs uppercase tracking-widest text-neutral-500">Stap 02 / 03</span>
                  <h3 className="text-xl md:text-2xl text-white font-display mt-2">PRIMAIR DOEL</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
                  <OptionCard icon={Shield} title="Bescherming" selected={state.goal === 'protection'} onClick={() => setState(p => ({ ...p, goal: 'protection' }))} />
                  <OptionCard icon={Sparkles} title="Esthetiek" selected={state.goal === 'aesthetics'} onClick={() => setState(p => ({ ...p, goal: 'aesthetics' }))} />
                  <OptionCard icon={Banknote} title="Verkoop / Herstel" selected={state.goal === 'resale'} onClick={() => setState(p => ({ ...p, goal: 'resale' }))} />
                </div>
                <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-6 mt-auto pt-4 md:pt-12">
                  <button onClick={() => setState(p => ({...p, step: p.step - 1}))} className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white transition-colors py-2">
                    Terug
                  </button>
                  <Button onClick={nextStep} disabled={!state.goal} className="w-full md:w-auto">
                    Volgende Stap <ArrowRight size={16} />
                  </Button>
                </div>
              </motion.div>
            )}

            {state.step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-grow flex flex-col"
              >
                 <div className="mb-8 md:mb-10">
                  <span className="text-xs uppercase tracking-widest text-neutral-500">Stap 03 / 03</span>
                  <h3 className="text-xl md:text-2xl text-white font-display mt-2">BUDGET INDICATIE</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
                  <OptionCard icon={Banknote} title="Entry Level" selected={state.budget === 'entry'} onClick={() => setState(p => ({ ...p, budget: 'entry' }))} />
                  <OptionCard icon={Banknote} title="Mid-Range" selected={state.budget === 'mid'} onClick={() => setState(p => ({ ...p, budget: 'mid' }))} />
                  <OptionCard icon={Banknote} title="High-End" selected={state.budget === 'premium'} onClick={() => setState(p => ({ ...p, budget: 'premium' }))} />
                </div>
                <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-6 mt-auto pt-4 md:pt-12">
                   <button onClick={() => setState(p => ({...p, step: p.step - 1}))} className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white transition-colors py-2">
                    Terug
                  </button>
                  <Button onClick={nextStep} disabled={!state.budget} className="w-full md:w-auto">
                    Toon Resultaat <ArrowRight size={16} />
                  </Button>
                </div>
              </motion.div>
            )}

            {state.step === 4 && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-neutral-900 border border-white/10 p-6 md:p-12 relative overflow-hidden h-full flex flex-col justify-center rounded-sm"
              >
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
                
                <div className="relative z-10">
                  {/* Summary of choices */}
                  <div className="flex gap-3 mb-6 flex-wrap">
                    {[state.carType, state.goal, state.budget].map((item, i) => (
                      <span key={i} className="text-[10px] uppercase tracking-widest bg-white/5 border border-white/5 px-3 py-1 text-neutral-400">
                        {item}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-col md:flex-row justify-between items-start mb-8 md:mb-12 border-b border-white/10 pb-6 md:pb-8 gap-6">
                    <div>
                      <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3 block">Ons Advies</span>
                      <h3 className="text-2xl md:text-5xl text-white font-display leading-tight">{getRecommendation().title}</h3>
                    </div>
                    <div className="text-left md:text-right">
                       <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2 block">Investering</span>
                       <div className="text-xl md:text-3xl text-white font-display">{getRecommendation().priceRange}</div>
                       <div className="text-neutral-600 text-xs mt-1">Excl. BTW</div>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-8 md:mb-12">
                    <div>
                      <span className="text-xs uppercase tracking-widest text-neutral-500 mb-4 md:mb-6 block">Behandeling</span>
                      <p className="text-neutral-300 leading-relaxed font-light text-base md:text-lg">
                        {getRecommendation().description}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-widest text-neutral-500 mb-4 md:mb-6 block">Specificaties</span>
                      <ul className="grid grid-cols-1 gap-3">
                        {getRecommendation().features.map((f, i) => (
                          <li key={i} className="flex items-center gap-4 text-neutral-300">
                            <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                            <span className="font-light">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                    <Button onClick={handleConversion} className="bg-white text-black hover:bg-neutral-200 w-full md:w-auto">
                      Aanvraag Starten <Mail size={16} className="ml-2" />
                    </Button>
                    <button onClick={reset} className="flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-widest text-neutral-500 hover:text-white transition-colors w-full md:w-auto">
                      <RotateCcw size={14} /> Nieuwe Configuratie
                    </button>
                  </div>

                  {/* Fixels Sales Hint - Premium & Discrete */}
                  <FixelsHint variant="dark" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
};

export default TreatmentSelector;